import { useFormHandler } from ".";
import { useCallback } from "react";
import { TECHNICAL_DEFINITION_DEFAULTS } from "../components";
import { slugify } from "@devowl-wp/react-utils";
import { useI18n } from "../contexts";
import { EServiceTemplateLegalBasis } from "@devowl-wp/api-real-cookie-banner";
function formServiceAttributesFromTemplate(template, param) {
    let { groups } = param;
    var _groups_filter_, _groups_filter;
    // Determine the group to use for a given preset, otherwise use current opened group tab
    const useGroup = template ? template.group ? ((_groups_filter = groups.filter((param)=>{
        let { name } = param;
        return name === template.group;
    })) == null ? void 0 : (_groups_filter_ = _groups_filter[0]) == null ? void 0 : _groups_filter_.id) || "preset-group-not-found" : -1 : undefined;
    const result = {
        name: template == null ? void 0 : template.name,
        group: typeof useGroup === "number" ? useGroup : undefined,
        templateGroupNotFound: useGroup === "preset-group-not-found",
        purpose: template == null ? void 0 : template.purpose,
        isProviderCurrentWebsite: template == null ? void 0 : template.isProviderCurrentWebsite,
        provider: template == null ? void 0 : template.provider,
        providerContact: (template == null ? void 0 : template.providerContact) ? {
            phone: "",
            email: "",
            link: "",
            ...JSON.parse(JSON.stringify(template.providerContact))
        } : undefined,
        providerPrivacyPolicyUrl: template == null ? void 0 : template.providerPrivacyPolicyUrl,
        providerLegalNoticeUrl: template == null ? void 0 : template.providerLegalNoticeUrl,
        uniqueName: template == null ? void 0 : template.identifier,
        isEmbeddingOnlyExternalResources: template == null ? void 0 : template.isEmbeddingOnlyExternalResources,
        dataProcessingInCountries: (template == null ? void 0 : template.dataProcessingInCountries) ? JSON.parse(JSON.stringify(template.dataProcessingInCountries)) : undefined,
        dataProcessingInCountriesSpecialTreatments: (template == null ? void 0 : template.dataProcessingInCountriesSpecialTreatments) ? JSON.parse(JSON.stringify(template.dataProcessingInCountriesSpecialTreatments)) : undefined,
        legalBasis: template == null ? void 0 : template.legalBasis,
        technicalDefinitions: (template == null ? void 0 : template.technicalDefinitions) ? JSON.parse(JSON.stringify(template.technicalDefinitions)) : undefined,
        tagManagerOptInEventName: template == null ? void 0 : template.tagManagerOptInEventName,
        tagManagerOptOutEventName: template == null ? void 0 : template.tagManagerOptOutEventName,
        googleConsentModeConsentTypes: (template == null ? void 0 : template.googleConsentModeConsentTypes) ? JSON.parse(JSON.stringify(template.googleConsentModeConsentTypes)) : undefined,
        executePriority: (template == null ? void 0 : template.executePriority) || 10,
        codeOptIn: template == null ? void 0 : template.codeOptIn,
        executeCodeOptInWhenNoTagManagerConsentIsGiven: template == null ? void 0 : template.executeCodeOptInWhenNoTagManagerConsentIsGiven,
        codeOptOut: template == null ? void 0 : template.codeOptOut,
        codeOnPageLoad: template == null ? void 0 : template.codeOnPageLoad,
        executeCodeOptOutWhenNoTagManagerConsentIsGiven: template == null ? void 0 : template.executeCodeOptOutWhenNoTagManagerConsentIsGiven,
        deleteTechnicalDefinitionsAfterOptOut: template == null ? void 0 : template.deleteTechnicalDefinitionsAfterOptOut
    };
    // Remove undefined and nulled values as the result of this function should only contain
    // real values so it can be used e.g. with `Object.assign`.
    for(const key in result){
        if ([
            "group"
        ].indexOf(key) === -1 && (result[key] === undefined || result[key] === null)) {
            delete result[key];
        }
    }
    return result;
}
function useFormServiceHandler(opts) {
    var _template_consumerData_technicalHandlingIntegration;
    const { __ } = useI18n();
    const { setCookiesViaManager, isGcm, territorialLegalBasis, selectedGroup, groups, allowContentBlockerCreation, shouldUncheckContentBlockerCheckbox, template, attributes, isEdit } = opts;
    const contentBlockerTemplates = (template == null ? void 0 : template.consumerData.contentBlockerTemplates) || [];
    const { templateGroupNotFound, ...defaultValuesFromTemplate } = formServiceAttributesFromTemplate(template, {
        groups
    });
    const defaultValues = {
        // General
        name: "",
        // Can be `0`, so fallback to no selection (e.g. Cookie form modal in Content Blocker)
        group: selectedGroup || undefined,
        status: "publish",
        purpose: "",
        isProviderCurrentWebsite: false,
        provider: "",
        providerContact: {
            phone: "",
            email: "",
            link: ""
        },
        providerPrivacyPolicyUrl: "",
        providerLegalNoticeUrl: "",
        uniqueName: "",
        isEmbeddingOnlyExternalResources: false,
        dataProcessingInCountries: [],
        dataProcessingInCountriesSpecialTreatments: [],
        legalBasis: EServiceTemplateLegalBasis.Consent,
        technicalDefinitions: [
            TECHNICAL_DEFINITION_DEFAULTS
        ],
        codeDynamics: {},
        tagManagerOptInEventName: "",
        tagManagerOptOutEventName: "",
        googleConsentModeConsentTypes: [],
        codeOptIn: "",
        executeCodeOptInWhenNoTagManagerConsentIsGiven: false,
        codeOptOut: "",
        codeOnPageLoad: "",
        executeCodeOptOutWhenNoTagManagerConsentIsGiven: false,
        deleteTechnicalDefinitionsAfterOptOut: false,
        createContentBlocker: (template == null ? void 0 : (_template_consumerData_technicalHandlingIntegration = template.consumerData.technicalHandlingIntegration) == null ? void 0 : _template_consumerData_technicalHandlingIntegration.name) ? false : typeof (attributes == null ? void 0 : attributes.createContentBlocker) === "boolean" ? attributes.createContentBlocker : contentBlockerTemplates.length > 0 && allowContentBlockerCreation && !shouldUncheckContentBlockerCheckbox,
        createContentBlockerId: attributes == null ? void 0 : attributes.createContentBlockerId,
        templateCheck: !template,
        succeessorDeletionCheck: (template == null ? void 0 : template.consumerData.successorOf.length) > 0 && !isEdit,
        ...defaultValuesFromTemplate,
        ...attributes || {}
    };
    // Reset group to empty value (e.g. VG Wort)
    defaultValues.group = defaultValues.group === -1 ? undefined : defaultValues.group;
    const handlers = useFormHandler({
        ...opts,
        defaultValues,
        i18n: {
            successMessage: __("You have successfully saved the service."),
            validationError: __("The service could not be saved due to missing/invalid form values."),
            unloadConfirm: __("You have unsaved changes. If you leave this page, your changes will be discarded.")
        }
    });
    const { form, setIsBusy, isTemplateUpdate, templateCheck } = handlers;
    // Overwrite onFinish to handle empty unique name and automatically generate it
    const onFinish = useCallback(async (values)=>{
        const { uniqueName, name } = values;
        if (!uniqueName) {
            setIsBusy(true);
            form.setFieldsValue({
                uniqueName: slugify(name)
            });
            const { uniqueName } = await form.validateFields([
                "uniqueName"
            ]);
            values.uniqueName = uniqueName;
        }
        handlers.onFinish(values);
    }, [
        handlers.onFinish,
        form,
        setIsBusy
    ]);
    return {
        ...handlers,
        template,
        onFinish: onFinish,
        defaultValues,
        contextValue: {
            form,
            template,
            territorialLegalBasis,
            isEdit,
            isTemplateUpdate,
            templateCheck,
            setCookiesViaManager,
            isGcm,
            defaultTemplateValues: template ? defaultValues : {},
            allowLegalBasisLegalRequirement: (template == null ? void 0 : template.identifier) === "real-cookie-banner",
            groups,
            preselectGroup: templateGroupNotFound ? "preset-group-not-found" : defaultValues.group,
            allowContentBlockerCreation,
            contentBlockerTemplates
        }
    };
}
export { useFormServiceHandler, formServiceAttributesFromTemplate };
